import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { rideCallStore } from "@/lib/rideCallStore";

// Gate 3.4 · ride calls reach the driver on any page; the trip page (RideCall) owns the call once mounted.
export function handleRideSignal(msg, send) {
  const rid = msg.ride_id, cid = msg.call_id, sig = msg.signal || {};
  if (!rid || !cid) return;
  if (msg.type === "ride_call_claimed") return rideCallStore.clear(rid, cid);
  if (sig.kind === "offer" && !sig.renegotiate) {
    if (rideCallStore.isMounted(rid)) return;
    if (rideCallStore.offer({ rideId: rid, callId: cid, from: msg.from_user_id, offer: sig.sdp })) {
      send({ type: "ride_webrtc", ride_id: rid, call_id: cid, signal: { kind: "ringing" } });
    }
  } else if (sig.kind === "ice") rideCallStore.addIce(rid, cid, sig.candidate);
  else if (["hangup", "decline", "busy"].includes(sig.kind)) rideCallStore.clear(rid, cid);
}

/**
 * Global push-notification listener — opens ONE messenger WebSocket per
 * signed-in session and surfaces server-side events as toasts across
 * every page. Auto-reconnects with a small backoff.
 *
 * Events handled:
 *   · delivery_status  { delivery_id, status }
 *   · trip_debit       { delivery_id, share_inr, fare_inr }
 *   · delivery_ping    (silent — RideTrack handles map updates itself)
 */
const STATUS_TOASTS = {
  assigned:          { msg: "Driver assigned to your trip", kind: "info" },
  picked_up:         { msg: "Driver has picked up your order", kind: "info" },
  in_transit:        { msg: "Your trip is on the way", kind: "info" },
  arrived_at_pickup: { msg: "Your driver has arrived at pickup", kind: "success" },
  delivered:         { msg: "Trip complete", kind: "success" },
  cancelled:         { msg: "Trip cancelled", kind: "warning" },
};

export function useGlobalPush(userId) {
  const wsRef       = useRef(null);
  const cancelledRef = useRef(false);
  const retryRef     = useRef(0);

  useEffect(() => {
    cancelledRef.current = false;
    let closeTimer = null;
    const connect = async () => {
      if (!userId || cancelledRef.current) return;
      try {
        const t = await api.get("/messenger/ws-ticket");
        if (cancelledRef.current || !t.data?.ticket) return;
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
        const ws = new WebSocket(`${base}/api/messenger/ws?ticket=${t.data.ticket}`);
        wsRef.current = ws;
        const send = (m) => ws.readyState === 1 && (ws.send(JSON.stringify(m)), true);
        rideCallStore.setSender(send);
        ws.onopen = () => { retryRef.current = 0; };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "ride_webrtc" || msg.type === "ride_call_claimed") handleRideSignal(msg, send);
            else if (msg.type === "delivery_status") {
              const meta = STATUS_TOASTS[msg.status];
              if (meta) {
                const fn = toast[meta.kind] || toast;
                fn(meta.msg, {
                  description: `Trip ${msg.delivery_id?.slice?.(-6) || ""}`,
                  action: { label: "Open", onClick: () => (window.location.href = `/ride/${msg.delivery_id}`) },
                });
              }
            } else if (msg.type === "trip_debit") {
              toast.info(`₹${msg.share_inr} charged to your wallet`, {
                description: `Split trip · fare ₹${msg.fare_inr}`,
              });
            } else if (msg.type === "weekly_trip_summary") {
              // Slice J · Sunday-night trip roundup.
              const s = msg.summary || {};
              toast.success(msg.title || "Weekly trip summary", {
                description: msg.body || `${s.trips || 0} trip(s) · ₹${s.spent_inr || 0}`,
                duration: 8000,
                action: { label: "View", onClick: () => (window.location.href = "/services?summary=1") },
              });
            }
          } catch { /* ignore malformed */ }
        };
        ws.onclose = () => {
          wsRef.current = null;
          rideCallStore.setSender(null);
          if (cancelledRef.current) return;
          const wait = Math.min(15_000, 1000 * 2 ** retryRef.current);
          retryRef.current += 1;
          closeTimer = setTimeout(connect, wait);
        };
        ws.onerror = () => { /* onclose will schedule reconnect */ };
      } catch { /* offline — skip */ }
    };
    connect();
    return () => {
      cancelledRef.current = true;
      if (closeTimer) clearTimeout(closeTimer);
      rideCallStore.setSender(null);
      rideCallStore.clearAll();                     // sign-out / user switch drops any pending ride call
      wsRef.current?.close();
    };
  }, [userId]);
}
